import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';

import * as service from '../services/sms-relay.service';
import type { SmsMessageStatus, SmsPurpose } from '../services/sms-relay.service';

const KEY = ['sms-relay'] as const;

export function useSmsRelayConfig() {
  return useQuery({ queryKey: [...KEY, 'config'], queryFn: service.getConfig });
}

export function useUpdateSmsRelayConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: service.updateConfig,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/**
 * Device e statistiche si aggiornano da soli: la pagina serve a capire se il
 * telefono è vivo adesso, un dato che invecchia in pochi secondi.
 */
export function useSmsRelayDevices() {
  return useQuery({
    queryKey: [...KEY, 'devices'],
    queryFn: service.getDevices,
    refetchInterval: 15_000,
  });
}

export function useSmsRelayStats() {
  return useQuery({
    queryKey: [...KEY, 'stats'],
    queryFn: service.getStats,
    refetchInterval: 15_000,
  });
}

export function useSmsRelayMessages(params: {
  page: number;
  status: SmsMessageStatus | '';
  purpose: SmsPurpose | '';
}) {
  return useQuery({
    queryKey: [...KEY, 'messages', params.page, params.status, params.purpose],
    queryFn: () => service.getMessages(params),
    placeholderData: keepPreviousData,
    refetchInterval: 15_000,
  });
}

export function useCreateSmsRelayDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: service.createDevice,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...KEY, 'devices'] });
    },
  });
}

export function useUpdateSmsRelayDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof service.updateDevice>[1] }) =>
      service.updateDevice(id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...KEY, 'devices'] });
    },
  });
}

export function useRotateSmsRelayKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: service.rotateDeviceKey,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...KEY, 'devices'] });
    },
  });
}

export function useDeleteSmsRelayDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: service.deleteDevice,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...KEY, 'devices'] });
    },
  });
}

export function useRetrySmsRelayMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, via }: { id: string; via?: 'relay' | 'ovh' }) => service.retryMessage(id, via),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useCancelSmsRelayMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: service.cancelMessage,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** Volume estero del mese: cambia di rado, basta un refetch al minuto. */
export function useSmsRelayIntlStats() {
  return useQuery({
    queryKey: [...KEY, 'intl-stats'],
    queryFn: () => service.getIntlStats(),
    refetchInterval: 60_000,
  });
}

export function useSendTestSms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ to, body }: { to: string; body?: string }) => service.sendTest(to, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
